import { createMemo, Show } from "solid-js";
import type { VoidComponent } from "solid-js";
import { isNil } from "lodash-es";
import { useTranslation } from "../../../../../state/i18n";
import CountriesService from "../../../../../services/CountriesService";
import { getCountryName } from "../../../../../utils/countryUtil";
import { SingleSelectWithSearchInput } from "../../../../../components/menu/SingleSelectWithSearch";
import { SettingTextValue } from "../../components/SettingValue";
import type { TextOption } from "../../../../../types/Menu";

type EditableCountryValueProps = {
  label: string;
  countryCode?: string;
  fallbackName: string;
  editing: boolean;
  editValue?: string;
  editError?: string;
  editOnSelect: (countryCode: string) => void;
  editDisabled?: boolean;
  required?: boolean;
};

const EditableCountryValue: VoidComponent<EditableCountryValueProps> = (props) => {
  const { currentLanguage } = useTranslation();
  const countriesQuery = CountriesService.getCountries();

  // Only read the countries once loaded, as reading them while loading suspends the whole page
  const options = createMemo(() =>
    (countriesQuery.isSuccess ? countriesQuery.data.countries : []).map(
      (country) =>
        ({
          variant: "text",
          value: country.alpha2Code,
          label: getCountryName(country, currentLanguage()),
        }) satisfies TextOption<string>,
    ),
  );

  const countryName = (): string =>
    options().find(({ value }) => value === props.countryCode)?.label ?? props.fallbackName;

  return (
    <Show when={props.editing} fallback={<SettingTextValue label={props.label}>{countryName()}</SettingTextValue>}>
      <SingleSelectWithSearchInput
        label={props.label}
        required={props.required}
        errorMessage={props.editError}
        options={{ countries: options() }}
        value={props.editValue}
        onSelect={(value) => {
          if (!isNil(value)) {
            props.editOnSelect(value);
          }
        }}
        width="matchTrigger"
        disabled={props.editDisabled || !countriesQuery.isSuccess}
      />
    </Show>
  );
};

export default EditableCountryValue;
